import { getApp, getApps } from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-app.js';
import { getAuth, onAuthStateChanged } from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-auth.js';
// uGqPRiwp — this module makes TWO authenticated calls (GET /orders and
// GET /job-status/download) and was the SEVENTH `Authorization` site in
// assets/, while the affordance guard only knew about six. Both of its 403s
// used to end in silence.
import { authedFetch, createStatusNote, renderStatus, showVerifyGate } from '@studio/verify-email';

// am4jHQaI — My Studio auth-state desync fix.
// This module is only loaded on /pages/my-studio when Liquid saw NO Shopify
// customer session. Sign-in on this store is Firebase (/pages/signin, PR #33)
// and never mints a Shopify session, so the server-rendered gate used to be a
// dead end for signed-in users. Here we observe the same Firebase app instance
// the header menu (firebase-auth.js) and /pages/profile use, reveal the
// dashboard for a signed-in user, and render their orders from studio-backend
// GET /orders (Firebase ID token; backend enforces email_verified scoping).

const dashboard = document.querySelector('[data-studio-dashboard][hidden]');

// Guard against init-order changes: if firebase-auth.js hasn't run getApp()'s
// initializeApp() yet, getApp() throws and strands the gate. getApps().length
// tells us whether a default app exists before we reach for it.
if (dashboard && getApps().length) {
  const auth = getAuth(getApp());

  onAuthStateChanged(auth, (user) => {
    const gate = document.querySelector('[data-studio-gate]');

    if (!user) {
      // Signed out (including a logout on an already-open tab) — re-show the
      // server-rendered gate and re-hide the dashboard so we don't leak a
      // signed-in view to a signed-out session.
      dashboard.hidden = true;
      if (gate) {
        gate.hidden = false;
        gate.style.display = '';
      }
      return;
    }

    // .ed-studio-gate carries display:flex, which defeats the [hidden] UA rule
    // (author styles always win) — hide with an inline display too.
    if (gate) {
      gate.hidden = true;
      gate.style.display = 'none';
    }
    dashboard.hidden = false;

    loadOrders().catch((err) => {
      // Dashboard (composer + carousel) stays usable without the order list.
      console.warn('[my-studio] orders unavailable:', err.message);
      // ...but an EMPTY order list and a REFUSED one looked identical, which is
      // the "failure indistinguishable from success" class this card is about.
      // An unverified password-provider customer saw no orders and no reason.
      if (err.status === 403) showVerifyGate(ordersNote(), 'orders');
    });
  });
}

async function loadOrders() {
  const base = (dashboard.dataset.backendUrl || '').replace(/\/$/, '');
  if (!base) return;

  // Token, Authorization header and the retry-once on the email_verified
  // gate: authedFetch (studio-verify-email.js).
  const res = await authedFetch(base + '/orders', { method: 'GET' });
  if (!res.ok) {
    // The status has to travel with the error — the caller cannot tell an
    // email_verified refusal from a transient failure without it.
    const e = new Error('GET /orders -> ' + res.status);
    e.status = res.status;
    throw e;
  }

  const { orders } = await res.json();
  const studioOrders = (orders || []).filter((order) =>
    lineItems(order).some((li) => propValue(li, 'Model')),
  );
  if (!studioOrders.length) return;

  const list = document.querySelector('[data-fb-orders]');
  const section = document.querySelector('[data-fb-orders-section]');
  if (!list || !section) return;

  list.textContent = '';
  studioOrders.forEach((order) => list.appendChild(orderRow(order)));
  section.hidden = false;
}

function lineItems(order) {
  return order?.lineItems?.nodes ?? [];
}

function propValue(li, name) {
  return (li.properties || []).find((p) => p.name === name && p.value)?.value || '';
}

// Mirrors the server-rendered Liquid row: number · models · status pill · date · action.
// DOM built with createElement/textContent — order fields are remote data,
// never interpolated into innerHTML (same XSS rule as firebase-auth.js).
function orderRow(order) {
  const row = el('div', 'ed-studio-order-row');

  row.appendChild(el('span', 'ed-studio-order-num', order.name || ''));

  const models = lineItems(order)
    .map((li) => propValue(li, 'Model'))
    .filter(Boolean)
    .join(', ');
  row.appendChild(el('span', 'ed-studio-order-models', models));

  // Status/result come from the opsagent-namespace order metafields the
  // backend job store writes (job-status route: status, output_url).
  const fulfilled = String(order.fulfillmentStatus || '').toUpperCase() === 'FULFILLED';
  const jobStatus = metafield(order, 'status');
  const statusKey =
    { 'in-progress': 'in-progress', ready: 'ready', delivered: 'delivered' }[jobStatus] ||
    (fulfilled ? 'delivered' : 'received');
  const labels = { 'in-progress': 'In progress', ready: 'Ready', delivered: 'Delivered' };
  row.appendChild(
    el('span', 'ed-studio-pill ed-studio-pill--' + statusKey, labels[statusKey] || 'Received'),
  );

  const created = order.createdAt ? new Date(order.createdAt) : null;
  const date = created && !isNaN(created)
    ? created.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : '';
  row.appendChild(el('span', 'ed-studio-order-date', date));

  const action = el('div', 'ed-studio-order-action');
  const legacyResult = lineItems(order)
    .map((li) => propValue(li, 'Result'))
    .find(Boolean);
  const outputUrl = metafield(order, 'output_url');
  const orderId = String(order.id || '').split('/').pop();

  if (legacyResult && /^https:\/\//.test(legacyResult)) {
    // Legacy public 'Result' URL — link directly.
    const link = el('a', 'ed-studio-btn ed-studio-btn--sm', 'Download');
    link.href = legacyResult;
    link.target = '_blank';
    link.rel = 'noopener';
    action.appendChild(link);
  } else if (outputUrl && orderId) {
    // 8ErSAlH4 — the delivered asset lives in a uniform-access (private) GCS
    // bucket, so output_url is NOT directly downloadable (a bare link 403s).
    // Fetch an owner-scoped, short-lived signed URL from /job-status/download
    // on click (Firebase ID token), then open it.
    const btn = el('button', 'ed-studio-btn ed-studio-btn--sm', 'Download');
    btn.type = 'button';

    // The status line the 403 message and its resend control are rendered into.
    // Relabelling the button was the dead end: "Retry" on a permanent refusal
    // is an instruction that can never succeed.
    const note = createStatusNote('data-download-note', 'ed-studio-download-note');

    btn.addEventListener('click', async () => {
      if (btn.disabled) return;
      btn.disabled = true;
      const label = btn.textContent;
      btn.textContent = 'Preparing…';
      try {
        const base = (dashboard.dataset.backendUrl || '').replace(/\/$/, '');
        // authedFetch guards the signed-out case (it throws 'not-signed-in'
        // rather than dereferencing a null currentUser — sign out in another
        // tab used to leave this button reading "Retry" forever) and retries
        // once on the email_verified gate.
        const res = await authedFetch(
          base + '/job-status/download?orderId=' + encodeURIComponent(orderId),
          { method: 'GET' }
        );
        // Was missing entirely, while the sibling at loadOrders() checks. A 403
        // body has no `.url`, so `data.url` was falsy, the code fell through to
        // re-enabling the button, and the customer clicked Download forever with
        // literally nothing happening and no error anywhere.
        if (!res.ok) {
          const e = new Error('GET /job-status/download -> ' + res.status);
          e.status = res.status;
          throw e;
        }
        const data = await res.json();
        if (data && data.url) window.open(data.url, '_blank', 'noopener');
        renderStatus(note, '', false, res.status);
        btn.textContent = label;
        btn.disabled = false;
      } catch (e) {
        if (e.status === 403) {
          showVerifyGate(note, 'downloads');
          btn.textContent = label;
          btn.disabled = false;
        } else {
          renderStatus(note, '', false, e.status);
          btn.textContent = 'Retry';
          setTimeout(() => { btn.textContent = label; btn.disabled = false; }, 3000);
        }
      }
    });
    action.appendChild(btn);
    action.appendChild(note);
  } else {
    // Parity with the server-rendered row: no result yet → a 'Details' link to
    // the order status / account page rather than an empty action cell.
    const detailsUrl = order.statusUrl || order.orderStatusUrl || order.customerUrl;
    if (detailsUrl && /^(https:\/\/|\/(?!\/))/.test(detailsUrl)) {
      const link = el('a', 'ed-studio-text-link', 'Details ›');
      link.href = detailsUrl;
      action.appendChild(link);
    }
  }
  row.appendChild(action);

  return row;
}

/**
 * The status line for order-list failures. Created on demand and reused, so a
 * second auth transition does not stack a second message.
 *
 * The orders section is `hidden` until there is something to show; a refusal is
 * something to show, so it is revealed here.
 */
function ordersNote() {
  const section = document.querySelector('[data-fb-orders-section]');
  const host = section || dashboard;
  if (!host) return null;
  let note = host.querySelector('[data-orders-note]');
  if (!note) {
    note = createStatusNote('data-orders-note', 'ed-studio-orders-note');
    host.insertBefore(note, host.firstChild);
  }
  if (section) section.hidden = false;
  return note;
}

function metafield(order, key) {
  return (order?.metafields?.nodes ?? []).find((m) => m.key === key && m.value)?.value || '';
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}
